import { type Currency, formatAmount, GIFT_CODE_TYPE_LABELS } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { DataTable } from "@loot-os/ui/components/data-table";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { createDataTableColumns } from "@loot-os/ui/lib/dataTable";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { ErrorAlert } from "../components/ErrorAlert";
import { GenerateGiftCodesDialog } from "../components/GenerateGiftCodesDialog";
import { LoadMoreButton } from "../components/LoadMoreButton";
import { ShortDate } from "../components/ShortDate";
import { amountColumnHeader, amountInputValue, useCurrency } from "../lib/currency";
import { GIFT_CODE_STATE_BADGE, type GiftCodeBatch, giftCodeBatchState } from "../lib/giftCodes";
import { useTRPC } from "../lib/trpc";

const batchColumns = createDataTableColumns<GiftCodeBatch>();

function buildBatchColumns(currency: Currency, tenantId: string, now: Date) {
  return batchColumns.columns([
    batchColumns.accessor((batch) => GIFT_CODE_TYPE_LABELS[batch.type], {
      id: "type",
      header: "Type",
      cell: ({ getValue, row }) => (
        <Link
          to="/tenants/$tenantId/storefront/gift-codes/$batchId"
          params={{ tenantId, batchId: row.original.id }}
          className="font-medium hover:underline"
        >
          {getValue()}
        </Link>
      ),
      meta: { filter: "text" },
    }),
    batchColumns.accessor("amountCents", {
      header: amountColumnHeader("Amount", currency),
      cell: ({ getValue }) => formatAmount(getValue(), currency),
      meta: { align: "end", csv: (value) => amountInputValue(Number(value), currency) },
    }),
    batchColumns.accessor("codeCount", {
      header: "Codes",
      meta: { align: "end", filter: "number" },
    }),
    batchColumns.accessor("redeemedCount", {
      header: "Redeemed",
      meta: { align: "end", filter: "number" },
    }),
    batchColumns.accessor((batch) => giftCodeBatchState(batch, now), {
      id: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={GIFT_CODE_STATE_BADGE[getValue()]}>{getValue()}</Badge>
      ),
      meta: { filter: "text" },
    }),
    batchColumns.accessor("expiresAt", {
      header: "Expires",
      cell: ({ getValue }) => {
        const expiresAt = getValue();
        return expiresAt ? (
          <ShortDate value={expiresAt} />
        ) : (
          <span className="text-muted-foreground">Never</span>
        );
      },
      meta: { csv: (value) => (typeof value === "string" ? value : "") },
    }),
    batchColumns.accessor("createdAt", {
      header: "Created",
      cell: ({ getValue }) => <ShortDate value={getValue()} />,
    }),
    batchColumns.accessor("note", {
      header: "Note",
      size: 280,
      cell: ({ getValue }) => (
        <span className="block truncate text-muted-foreground" title={getValue()}>
          {getValue() || "—"}
        </span>
      ),
      meta: { filter: "text" },
    }),
  ]);
}

/**
 * Gift codes (boxed parity: the Gift Code Manager): the environment's batches, newest first, and
 * the form that generates one. A batch's own page lists its codes and downloads them.
 */
export function GiftCodesPage() {
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId/storefront/gift-codes" });
  const trpc = useTRPC();
  const currency = useCurrency();
  const [now] = useState(() => new Date());
  const batches = useInfiniteQuery(
    trpc.giftCodes.batches.infiniteQueryOptions(
      { limit: 50 },
      { getNextPageParam: (last) => last.nextCursor },
    ),
  );
  const rows = useMemo(
    () => batches.data?.pages.flatMap((page) => page.data) ?? [],
    [batches.data],
  );
  const columns = useMemo(
    () => buildBatchColumns(currency, tenantId, now),
    [currency, tenantId, now],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Gift codes</h1>
          <p className="text-sm text-muted-foreground">
            Balance codes shoppers redeem on their account page, generated in batches. Each code
            redeems once, in this environment only.
          </p>
        </div>
        <GenerateGiftCodesDialog />
      </div>

      {batches.error ? (
        <ErrorAlert message={batches.error.message} />
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3">
            {batches.isPending ? (
              <Skeleton className="h-40" />
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(batch) => batch.id}
                search="Search batches"
                csvName="gift-code-batches"
                emptyMessage="No gift codes yet. Generate a batch to hand some out."
              />
            )}
            <LoadMoreButton
              hasNextPage={batches.hasNextPage}
              isFetchingNextPage={batches.isFetchingNextPage}
              onClick={() => void batches.fetchNextPage()}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
